import { useLingui } from '@lingui/react/macro';
import { SearchNormal1, Star1 } from 'iconsax-reactjs';
import { type FC, useMemo, useState } from 'react';
import { useHasAccessToken } from '@/auth/api';
import { usePairs } from '@/shared/api';
import { Badge } from '@/shared/Badge';
import { Coin } from '@/shared/Coin';
import { Input } from '@/shared/Input';
import { cn } from '@/shared/react';
import { Table, type TableColumn } from '@/shared/Table';
import { useFeeTiers, useMyTradeFees } from './api';
import { FeeRate } from './FeeRate';

type TierRow = {
  key: string;
  name: string;
  description: string;
  maker_rate?: string;
  taker_rate?: string;
  is_default: boolean;
  isMine: boolean;
};

// Only the requester's own tier has published per-pair rates. Every other tier
// falls back to its defaults, which is what it charges on any pair it does not
// override — see the footnote under the table.
const MakerTaker: FC<{ maker?: string; taker?: string }> = ({
  maker,
  taker,
}) => (
  <div className="flex items-center gap-1 w-full justify-center">
    <FeeRate value={maker} className="w-10 text-end" />
    <span className="text-content-tertiary opacity-20">|</span>
    <FeeRate value={taker} className="w-10 text-start" />
  </div>
);

const MakerTakerTitle: FC = () => {
  const { t } = useLingui();
  return (
    <div className="flex items-center gap-1 w-full justify-center border-t border-t-border-disabled/75 pt-0.5 mt-0.5">
      <span className="w-11 text-end">{t`Maker`}</span>
      <span className="text-content-tertiary opacity-20">|</span>
      <span className="w-11 text-start">{t`Taker`}</span>
    </div>
  );
};

export const FeeTable: FC<{ className?: string }> = ({ className }) => {
  const { t } = useLingui();
  const tiers = useFeeTiers();
  const myFees = useMyTradeFees();
  const pairs = usePairs();
  const hasAccessToken = useHasAccessToken();
  const [search, setSearch] = useState('');

  const myCode = myFees.data?.fee_class?.code;

  const rows = useMemo<TierRow[]>(() => {
    if (tiers.data?.length)
      return tiers.data.map((tier) => ({
        key: tier.code,
        name: tier.name,
        description: tier.description,
        maker_rate: tier.maker_rate,
        taker_rate: tier.taker_rate,
        is_default: tier.is_default,
        isMine: !!myCode && tier.code === myCode,
      }));
    // No tier published yet — the state the exchange ships in. Show the
    // requester's own rates so the page still answers "what do I pay?".
    if (!myFees.data) return [];
    return [
      {
        key: myFees.data.fee_class?.code ?? 'current',
        name: myFees.data.fee_class?.name ?? t`Standard rates`,
        description: myFees.data.fee_class?.description ?? '',
        is_default: true,
        isMine: true,
      },
    ];
  }, [tiers.data, myFees.data, myCode, t]);

  const myPairRates = useMemo(
    () => new Map((myFees.data?.fees ?? []).map((fee) => [fee.pair, fee])),
    [myFees.data],
  );

  const pairColumns = useMemo(() => {
    const bySymbol = new Map(
      (pairs.data ?? []).map((pair) => [pair.slug, pair]),
    );
    const query = search.trim().toUpperCase();
    return (myFees.data?.fees ?? [])
      .map((fee) => ({ symbol: fee.pair, pair: bySymbol.get(fee.pair) }))
      .filter(
        ({ symbol, pair }) =>
          !query ||
          [symbol, pair?.base_coin, pair?.quote_coin].some((field) =>
            field?.toUpperCase().includes(query),
          ),
      );
  }, [myFees.data, pairs.data, search]);

  const columns = useMemo<TableColumn<TierRow>[]>(
    () => [
      {
        key: 'name',
        title: t`Tier`,
        width: 'min',
        // Pinned so the tier stays readable while the pair columns scroll.
        className:
          'desktop:sticky desktop:start-0 desktop:z-20 bg-background-primary',
        render: (row) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{row.name}</span>
            {row.isMine && (
              <Badge size={4} variant="primary_bold">
                <Star1 size={12} variant="Bold" />
                {t`Yours`}
              </Badge>
            )}
            {!row.isMine && row.is_default && (
              <Badge size={4} variant="gray">{t`Default`}</Badge>
            )}
          </div>
        ),
      },
      {
        key: 'description',
        title: t`Description`,
        width: 'grow',
        render: (row) => (
          <span className="text-content-tertiary whitespace-normal">
            {row.description || '---'}
          </span>
        ),
      },
      {
        key: 'default',
        title: (
          <div className="flex flex-col gap-0.5 items-center">
            <span>{t`Standard`}</span>
            <MakerTakerTitle />
          </div>
        ),
        width: 'min',
        render: (row) => (
          <MakerTaker maker={row.maker_rate} taker={row.taker_rate} />
        ),
      },
      ...pairColumns.map(({ symbol, pair }) => ({
        key: symbol,
        title: (
          <div className="flex flex-col gap-0.5 items-center">
            {pair ? (
              <Coin
                size={5}
                coin={pair.base_coin}
                quote={pair.quote_coin}
                variant="symbol"
              />
            ) : (
              <span>{symbol}</span>
            )}
            <MakerTakerTitle />
          </div>
        ),
        width: 130,
        render: (row: TierRow) => {
          const mine = row.isMine ? myPairRates.get(symbol) : undefined;
          return (
            <MakerTaker
              maker={mine?.maker_rate ?? row.maker_rate}
              taker={mine?.taker_rate ?? row.taker_rate}
            />
          );
        },
      })),
    ],
    [t, pairColumns, myPairRates],
  );

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <Input
        size={10}
        block
        value={search}
        onChange={(value) => setSearch(value ?? '')}
        placeholder={t`Search a pair`}
        prefix={<SearchNormal1 size={16} className="text-content-tertiary" />}
        className="desktop:max-w-xs"
      />
      <div className="overflow-x-auto">
        <Table
          rows={rows}
          loading={tiers.isLoading || myFees.isLoading}
          columns={columns}
          rowKey={(row) => row.key}
          empty={
            myFees.error
              ? t`Fees are unavailable right now.`
              : t`No fee tier has been published yet.`
          }
          className="min-w-full"
        />
      </div>
      {!!search.trim() && pairColumns.length === 0 && (
        <p className="text-xs text-content-tertiary">
          {t`No pair matches your search.`}
        </p>
      )}
      <p className="text-xs text-content-tertiary leading-relaxed">
        {myCode
          ? t`Your own tier shows the exact rate you pay on each pair. Other tiers show their standard rates — a tier may price an individual pair lower than its standard.`
          : t`Each tier shows its standard rates. A tier may price an individual pair lower than its standard.`}
        {!hasAccessToken && ` ${t`Sign in to see the rates of your own tier.`}`}
      </p>
    </div>
  );
};
